import { type OperationVariables } from '@apollo/react-common';
import { type GraphQLTaggedNode } from 'react-relay';
import { type FetchQueryFetchPolicy } from 'relay-runtime';
import { type ApolloQueryResult } from 'apollo-client';

import { fetchQuery } from './fetchQuery';
import { toDocumentNode } from './toDocumentNode';
import { apolloCompatibleQuery } from './apolloCompatibleQuery';
import { type ApolloErrorInterop } from './ApolloErrorInterop';

export type QueryInteropSupportedResult<TData> = Pick<ApolloQueryResult<TData>, 'data' | 'errors'>;

export type QueryInteropSupportedArgs<
	_TData,
	TVariables extends Record<string, any> = OperationVariables,
> = {
	query: GraphQLTaggedNode;
	variables?: TVariables;
	errorPolicy?: 'none' | 'ignore' | 'all';
	fetchPolicy?: 'cache-first' | 'network-only' | 'cache-only' | 'no-cache';
};

export function queryInterop<TData, TVariables extends Record<string, any> = OperationVariables>(
	params: QueryInteropSupportedArgs<TData, TVariables>,
): Promise<QueryInteropSupportedResult<TData>> {
	return process.env.BUILD_VARIANT === 'relay'
		? queryInteropRelay<TData, TVariables>(params)
		: queryInteropApollo<TData, TVariables>(params);
}

function queryInteropRelay<TData, TVariables extends Record<string, any> = OperationVariables>(
	params: QueryInteropSupportedArgs<TData, TVariables>,
): Promise<QueryInteropSupportedResult<TData>> {
	const relayFetchPolicy: FetchQueryFetchPolicy = mapFetchPolicyForQuery(params.fetchPolicy);

	return apolloCompatibleQuery<TData>({
		fetchData: () =>
			fetchQuery<{ response: TData; variables: TVariables }>(
				window.GLOBAL_RELAY_ENV!,
				params.query,
				params.variables ?? ({} as TVariables),
				{ fetchPolicy: relayFetchPolicy },
			).toPromise() as Promise<TData>,
		convertToApolloError,
	});
}

function queryInteropApollo<TData, TVariables extends Record<string, any> = OperationVariables>({
	query,
	...rest
}: QueryInteropSupportedArgs<TData, TVariables>): Promise<QueryInteropSupportedResult<TData>> {
	return window['GLOBAL_APOLLO_CLIENT']!.query({
		...rest,
		query: toDocumentNode(query),
	});
}

// Relay:  https://relay.dev/docs/guided-tour/reusing-cached-data/fetch-policies/
// Apollo: https://www.apollographql.com/docs/react/data/queries#supported-fetch-policies
function mapFetchPolicyForQuery(
	fetchPolicy: 'cache-first' | 'network-only' | 'cache-only' | 'no-cache' | undefined,
): FetchQueryFetchPolicy {
	switch (fetchPolicy) {
		case 'no-cache':
		case 'network-only':
			return 'network-only';
		case 'cache-first':
		case 'cache-only': // Ideally, for 'cache-only' we should just attempt to do a store read. In the absence of `readQuery` shim, we're temporarily defaulting to `'store-or-network'`
		default:
			return 'store-or-network';
	}
}

export function convertToApolloError(error: Error): ApolloErrorInterop {
	// TODO: Implement proper error conversion
	return error as ApolloErrorInterop;
}
